import React from 'react';

import { Col, Dropdown, Menu, notification, Row } from 'antd';
import routePaths from 'config/routes';
import { NextPage } from 'next';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { logout } from 'src/actions/authActions';
import Link from 'src/components/Link';
import { IRootReducers } from 'src/reducers';
import { getUserData } from 'utils';

interface OwnProps {
  cartItems: number;
}

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const SignInHeader: NextPage<Props, {}> = ({
  accessToken,
  cartItems,
  logout,
  user,
  userPoints,
  userStatus,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  const userData = getUserData(user);

  const onLogout = async () => {
    try {
      await logout();

      notification['success']({
        message: formatMessage({ id: 'app.logout' }),
      });
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        notification['error']({
          message: formatMessage({ id: 'app.logout-error' }),
          description: `${data.code} ${data.error}`,
        });
      }
      console.error(error);
    }
  };

  const dropdownMenu = (
    <Menu>
      <Menu.Item>
        <Link href={routePaths.PAGES.MY_ACCOUNT.ACCOUNT_INDEX}>
          <a>{formatMessage({ id: 'menu.header.my-account' })}</a>
        </Link>
      </Menu.Item>
      <Menu.Item onClick={onLogout}>
        <a>{formatMessage({ id: 'menu.header.logout' })}</a>
      </Menu.Item>
    </Menu>
  );

  const checkoutCart = (
    <a className="link basicHeader__signin__icon--container">
      <div className="basicHeader__signin__icon basicHeader__signin__icon--bag " />
      {formatMessage({ id: 'menu.header.bag' }, { items: cartItems })}
    </a>
  );

  return (
    <div className="basicHeader__signin">
      <Row justify="end" className="container basicHeader__signin__container">
        <Col className="basicHeader__signin__col">
          <Link
            href={
              accessToken
                ? user?.salesforce?.centerDelegates?.length
                  ? routePaths.PAGES.CONTACT_REP
                  : routePaths.FOOTER.CONTACT_US
                : routePaths.PAGES.LOGIN
            }
          >
            <a className="link">
              {formatMessage({ id: 'menu.header.contact' })}
            </a>
          </Link>
        </Col>

        <Col className="basicHeader__signin__wrapper basicHeader__signin__col">
          {user ? (
            <Dropdown
              overlay={dropdownMenu}
              className="basicHeader__signin__dropdown"
            >
              <div className="link">
                <Link
                  href={
                    accessToken
                      ? routePaths.PAGES.MY_ACCOUNT.ACCOUNT_INDEX
                      : routePaths.PAGES.LOGIN
                  }
                >
                  <a className="basicHeader__signin__icon basicHeader__signin__icon--user" />
                </Link>
                <span>
                  {formatMessage(
                    { id: 'menu.header.hello' },
                    {
                      name: userData.name,
                      surname: userData.surname,
                    }
                  )}
                  <b style={{ fontWeight: 600 }}>
                    {typeof userPoints === 'number' &&
                      formatMessage(
                        { id: 'menu.header.points' },
                        { points: Number(userPoints).toLocaleString() }
                      )}
                  </b>
                </span>
              </div>
            </Dropdown>
          ) : (
            <Link
              href={
                accessToken
                  ? routePaths.PAGES.MY_ACCOUNT.ACCOUNT_INDEX
                  : routePaths.PAGES.LOGIN
              }
            >
              <a className="basicHeader__signin__icon basicHeader__signin__icon--user" />
            </Link>
          )}
        </Col>
        <Col className="basicHeader__signin__col">
          <div className="link basicHeader__signin__wrapper">
            {checkoutCart}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = (state: IRootReducers) => {
  return {
    user: state.auth.user,
    accessToken: state.auth.accessToken,
    userPoints: state.auth.userPoints,
    userStatus: state.auth.userStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ logout }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SignInHeader);
